<template>
  <div class="card">
    <div class="card-body">
      <div class="font-weight-bold mb-2">
        Contractor
      </div>

      <v-select
        ref="contractorSelect"
        v-model="selectedContractorId"
        class="sm contractor-select"
        :options="contractorOptions"
        label="name"
        :reduce="getContractorReduce"
        :clearable="true"
        :disabled="contractors.loading"
        placeholder="Select Contractor"
        @open="onContractorOpen"
        @search="onContractorSearch"
      />

      <div class="donut-wrap">
        <highcharts :options="chartOptions" />
        <div class="donut-center">
          <div
            class="donut-center-top"
            :title="`${averageSubmitterText} Average Submitter`"
          >
            {{ averageSubmitterText }}
          </div>
          <div class="donut-center-bottom">
            {{ totalContractorText }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VSelect from 'vue-select'
import Api from '@/utils/Api'

export default {
  components: {
    VSelect
  },

  props: {
    label: {
      type: String,
      default: 'All Contractor'
    },
    contractorId: {
      type: [String, Number],
      default: null
    },
    progress: {
      type: Number,
      default: null
    },
    value: {
      type: Number,
      required: true
    },
    total: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    contractors: {
      all: [],
      loading: false,
      pageSize: 10,
      page: 1,
      search: ''
    },
    contractorsDropdownEl: null,
    selectedContractorId: null,
    chartOptions: {
      chart: {
        type: 'pie',
        height: 220,
        spacing: [0, 0, 0, 0]
      },
      title: false,
      credits: false,
      tooltip: {
        enabled: false
      },
      plotOptions: {
        pie: {
          size: '82%',
          innerSize: '72%',
          startAngle: -90,
          endAngle: 270,
          dataLabels: {
            enabled: false
          },
          borderWidth: 0
        }
      },
      series: [{
        data: []
      }]
    }
  }),

  computed: {
    contractorOptions () {
      const search = (this.contractors.search || '').trim().toLowerCase()
      const all = this.contractors.all || []
      const filtered = search
        ? all.filter(item => String(item?.name || '').toLowerCase().includes(search))
        : all

      const limit = (Number(this.contractors.page) || 1) * (Number(this.contractors.pageSize) || 10)
      const sliced = filtered.slice(0, limit)
      return [{ id: '__all__', name: 'All Contractor' }, ...sliced]
    },
    averageSubmitterText () {
      const value = Number(this.value) || 0
      return Number.isInteger(value) ? String(value) : String(value.toFixed(2))
    },
    totalContractorText () {
      const total = Number(this.total) || 0
      return Number.isInteger(total) ? String(total) : String(Math.round(total))
    },
    progressPercent () {
      const total = Number(this.total) || 0
      const value = Number(this.value) || 0

      const p = this.progress === null || this.progress === undefined
        ? (total > 0 ? (value / total) * 100 : 0)
        : (Number(this.progress) || 0)

      return Math.min(100, Math.max(0, p))
    }
  },

  watch: {
    label: {
      immediate: true,
      handler (val) {
        if (!this.contractorId && val && val !== 'All Contractor') {
          const found = (this.contractors.all || []).find(c => c?.name === val)
          this.selectedContractorId = found?.id || null
        }
      }
    },
    contractorId: {
      immediate: true,
      handler (val) {
        this.selectedContractorId = val === null || val === undefined ? null : val
      }
    },
    selectedContractorId (val) {
      const id = val === '__all__' ? null : (val || null)
      const selected = (this.contractors.all || []).find(c => c?.id === id)
      const name = selected?.name || 'All Contractor'

      this.$emit('update:contractorId', id)
      this.$emit('update:label', name)
      this.$emit('change', { contractorId: id, label: name })
    },
    value: {
      immediate: true,
      handler () {
        this.setSeries()
      }
    },
    progress: {
      immediate: true,
      handler () {
        this.setSeries()
      }
    },
    total: {
      immediate: true,
      handler () {
        this.setSeries()
      }
    }
  },

  mounted () {
    this.fetchContractors()
  },

  beforeDestroy () {
    if (this.contractorsDropdownEl) {
      this.contractorsDropdownEl.removeEventListener('scroll', this.onContractorScroll)
      this.contractorsDropdownEl = null
    }
  },

  methods: {
    getContractorReduce (option) {
      return option?.id ?? null
    },
    async fetchContractors () {
      try {
        this.contractors.loading = true
        const { data } = await Api.masterData.customContractors()

        const raw = Array.isArray(data?.data)
          ? data.data
          : (Array.isArray(data?.data?.data) ? data.data.data : [])

        const items = raw
          .map(item => ({
            id: item?.id ?? item?.contractorId ?? null,
            name: item?.name ? String(item.name).trim() : ''
          }))
          .filter(item => item?.name)
          .filter(item => String(item.name).toLowerCase() !== 'all contractor')

        const uniqueById = items.reduce((acc, curr) => {
          const key = curr?.id || curr?.name
          if (!key) return acc
          if (acc.some(a => (a?.id && a.id === curr.id) || (!a?.id && a?.name === curr.name))) return acc
          return [...acc, curr]
        }, [])

        this.contractors.all = uniqueById.sort((a, b) => String(a.name).localeCompare(String(b.name)))
        this.contractors.page = 1

        if (this.contractorId) {
          this.selectedContractorId = this.contractorId
        }
      } catch {
        this.contractors.all = []
        this.contractors.page = 1
      } finally {
        this.contractors.loading = false
      }
    },
    onContractorSearch (search) {
      this.contractors.search = search || ''
      this.contractors.page = 1
    },
    onContractorOpen () {
      this.$nextTick(() => {
        const dropdownEl = this.$refs.contractorSelect?.$el?.querySelector('.vs__dropdown-menu')
        if (!dropdownEl) return

        if (this.contractorsDropdownEl && this.contractorsDropdownEl !== dropdownEl) {
          this.contractorsDropdownEl.removeEventListener('scroll', this.onContractorScroll)
        }

        this.contractorsDropdownEl = dropdownEl
        this.contractorsDropdownEl.removeEventListener('scroll', this.onContractorScroll)
        this.contractorsDropdownEl.addEventListener('scroll', this.onContractorScroll)
      })
    },
    onContractorScroll (e) {
      const el = e?.target
      if (!el) return

      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2
      if (!nearBottom) return

      const search = (this.contractors.search || '').trim().toLowerCase()
      const all = (this.contractors.all || [])
      const filteredCount = search
        ? all.filter(item => String(item?.name || '').toLowerCase().includes(search)).length
        : all.length

      const pageSize = Number(this.contractors.pageSize) || 10
      const currentLimit = (Number(this.contractors.page) || 1) * pageSize
      if (currentLimit >= filteredCount) return

      this.contractors.page = (Number(this.contractors.page) || 1) + 1
    },
    setSeries () {
      const filled = Math.round(this.progressPercent * 10) / 10
      const empty = Math.max(0, 100 - filled)
      this.chartOptions.series[0].data = [
        { name: 'Filled', y: filled, color: '#18B2BD' },
        { name: 'Empty', y: empty, color: '#DEDEDE' }
      ]
    }
  }
}
</script>

<style scoped>
.contractor-select {
  background-color: #f8f9fa;
  border: 1px solid #dedede;
  border-radius: 10px;
  font-size: 12px;
  margin-bottom: 10px;
  width: 100%;
}

.donut-wrap {
  position: relative;
}

.donut-center {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  left: 50%;
  pointer-events: none;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -42%);
}

.donut-center-top {
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
}

.donut-center-bottom {
  color: #18b2bd;
  font-size: 18px;
  font-weight: bold;
  margin-top: 6px;
}
</style>
